/**
 * Impact-regime knobs for `burst()`. Intensity is contact energy-density
 * (velocity × 1/area); blastFraction is the chemical share.
 *
 * Identity: omitted `impact` OR `kind:'cannon'` (intensity 0.25, blast 0)
 * leaves every existing planner bit-identical. All modulators are 1 or 0
 * at that point; extra emitters (back-spall, shock ring) return [].
 */
import type { CloudSpawnRecord } from './cloudSpawn';
import type { ChunkSpawnRecord } from './chunkSim';
import {
  clampEnergy,
  energyLaunch,
  energyRadius,
} from '../../services/energy';
import {
  coneAround,
  dot3,
  normalize3,
} from './impactFrame';
import type { ParticleMaterialProfile } from './profiles';
import { mulberry32, seedFromBurst } from './spawn';
import type { BoxCollider } from './types';

export const IMPACT_KINDS = ['bullet', 'cannon', 'missile'] as const;
export type ImpactKind = (typeof IMPACT_KINDS)[number];

export interface ImpactParams {
  intensity?: number;
  blastFraction?: number;
  kind?: ImpactKind;
}

export const CANNON_INTENSITY = 0.25;
export const CANNON_BLAST = 0;
export const SPALL_INTENSITY_THRESHOLD = 0.7;
/** Wall slab is 0.18 m through; cube / column / bench do not qualify. */
export const THIN_WALL_MAX_DEPTH = 0.28;
/** Hits this far outside an AABB still bind to that collider. */
export const HIT_BIND_DISTANCE = 0.35;

export const IMPACT_PRESETS: Record<ImpactKind, { intensity: number; blastFraction: number }> = {
  bullet: { intensity: 1, blastFraction: 0 },
  cannon: { intensity: CANNON_INTENSITY, blastFraction: CANNON_BLAST },
  missile: { intensity: 0.5, blastFraction: 0.85 },
};

export const RING_FINE_KIND = 6;
export const SPALL_FINE_KIND = 7;

export interface ResolvedImpact {
  readonly kind: ImpactKind;
  readonly intensity: number;
  readonly blastFraction: number;
  /** 0 at cannon intensity, 1 at intensity = 1. */
  readonly iHi: number;
  readonly identity: boolean;
}

function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  return Math.min(1, Math.max(0, x));
}

export function resolveImpact(impact?: ImpactParams | null): ResolvedImpact {
  const kind: ImpactKind = impact?.kind && IMPACT_PRESETS[impact.kind]
    ? impact.kind
    : 'cannon';
  const preset = IMPACT_PRESETS[kind];
  const intensity = impact?.intensity !== undefined ? clamp01(impact.intensity) : preset.intensity;
  const blastFraction = impact?.blastFraction !== undefined
    ? clamp01(impact.blastFraction)
    : preset.blastFraction;
  const iHi = clamp01((intensity - CANNON_INTENSITY) / (1 - CANNON_INTENSITY));
  return {
    kind,
    intensity,
    blastFraction,
    iHi,
    identity: intensity === CANNON_INTENSITY && blastFraction === CANNON_BLAST,
  };
}

/** Whole-brick count. 1 at cannon, 0 at a full-intensity bullet. */
export function impactChunkScale(imp: ResolvedImpact): number {
  return 1 - imp.iHi;
}

/** Grit count. High intensity pulverizes the small contact volume. */
export function impactGritScale(imp: ResolvedImpact): number {
  return 1 + imp.iHi * 0.28;
}

/**
 * Primary fine *count*. High intensity shrinks the contact volume (bullet
 * is a pock, not a crater); blast dumps a much larger cloud.
 */
export function impactFineCountScale(imp: ResolvedImpact): number {
  const kinetic = 1 - imp.iHi * 0.82;
  return kinetic * (1 + imp.blastFraction * 1.85);
}

/** Primary chip count. Smaller pock → fewer corner chips. */
export function impactChipScale(imp: ResolvedImpact): number {
  return 1 - imp.iHi * 0.55;
}

/** Joint-bleed / puff schedule. High intensity is too local to walk the grid. */
export function impactJointScale(imp: ResolvedImpact): number {
  return 1 - imp.iHi;
}

/** Spawn jitter / influence radius. High intensity → smaller deeper pock. */
export function impactStampScale(imp: ResolvedImpact): number {
  return 1 - imp.iHi * 0.78;
}

/** Launch speed. Intensity makes a fast jet; blast throws fragments farther. */
export function impactSpeedScale(imp: ResolvedImpact): number {
  return 1 + imp.iHi * 0.55 + imp.blastFraction * 0.85;
}

/** Extra directional spread from the chemical share. */
export function impactSpreadScale(imp: ResolvedImpact): number {
  return 1 + imp.blastFraction * 0.55;
}

/** Fine lifetime. Bullet dust is gone in well under a second. */
export function impactLingerScale(imp: ResolvedImpact): number {
  return 1 - imp.iHi * 0.55;
}

/** Grain visual deposit. Smaller stamp, more height per grain. */
export function impactDepositScale(imp: ResolvedImpact): number {
  return 1 + imp.iHi * 1.4;
}

/**
 * Blast raises the fines share of the leftover (non-chunk) mass.
 * 1 at blast 0.
 */
export function impactFineMassScale(imp: ResolvedImpact): number {
  return 1 + imp.blastFraction * 1.45;
}

/**
 * Cone half-angle: intensity narrows the jet; blast opens it toward a
 * hemisphere (π/2). Identity at cannon: returns `base` unchanged.
 */
export function mixImpactCone(base: number, imp: ResolvedImpact): number {
  const narrowed = base * (1 - imp.iHi * 0.82);
  return narrowed + (Math.PI * 0.5 - narrowed) * imp.blastFraction;
}

/** Wall-plane splash share. High intensity is too local; blast prefers the hemisphere. */
export function impactSplashScale(imp: ResolvedImpact): number {
  return Math.max(0, 1 - imp.iHi * 0.88 - imp.blastFraction * 0.7);
}

/**
 * Stored upFactor. Blast restores the floor mushroom / vortex on a wall
 * by lifting the GPU `upFactor` toward 1. Identity at blast 0.
 */
export function impactStoredUpFactor(upFactor: number, imp: ResolvedImpact): number {
  return upFactor + (1 - upFactor) * imp.blastFraction;
}

/** Haze fraction. Bullet is too local / short-lived for a lingering veil. */
export function impactHazeScale(imp: ResolvedImpact): number {
  return 1 - imp.iHi;
}

export function nearestCollider(
  pos: readonly [number, number, number],
  colliders: readonly BoxCollider[],
): BoxCollider | null {
  let best: BoxCollider | null = null;
  let bestD = Infinity;
  for (const b of colliders) {
    const dx = Math.max(Math.abs(pos[0] - b.center[0]) - b.half[0], 0);
    const dy = Math.max(Math.abs(pos[1] - b.center[1]) - b.half[1], 0);
    const dz = Math.max(Math.abs(pos[2] - b.center[2]) - b.half[2], 0);
    const d = Math.hypot(dx, dy, dz);
    if (d < bestD) {
      bestD = d;
      best = b;
    }
  }
  if (!best || bestD > HIT_BIND_DISTANCE) return null;
  return best;
}

/** Axis-aligned slab thickness along a unit normal (m). */
export function slabThicknessAlongNormal(
  box: BoxCollider,
  normal: readonly [number, number, number],
): number {
  const n = normalize3(normal);
  return 2 * (Math.abs(n[0]) * box.half[0] + Math.abs(n[1]) * box.half[1] + Math.abs(n[2]) * box.half[2]);
}

export function isThinHit(
  pos: readonly [number, number, number],
  normal: readonly [number, number, number],
  colliders: readonly BoxCollider[],
): boolean {
  const box = nearestCollider(pos, colliders);
  if (!box) return false;
  return slabThicknessAlongNormal(box, normal) <= THIN_WALL_MAX_DEPTH;
}

export function shouldBackSpall(imp: ResolvedImpact, thin: boolean): boolean {
  if (!thin) return false;
  return imp.intensity >= SPALL_INTENSITY_THRESHOLD || imp.blastFraction > 0;
}

export function shouldBothFaceSpall(imp: ResolvedImpact, thin: boolean): boolean {
  return thin && imp.blastFraction > 0;
}

function backOriginFromHit(
  pos: readonly [number, number, number],
  normal: readonly [number, number, number],
  box: BoxCollider,
): [number, number, number] {
  const n = normalize3(normal);
  const toCenter: [number, number, number] = [
    box.center[0] - pos[0],
    box.center[1] - pos[1],
    box.center[2] - pos[2],
  ];
  const alongN = dot3(toCenter, n);
  const halfT = slabThicknessAlongNormal(box, n) * 0.5;
  const travel = -alongN + halfT + 0.05;
  return [pos[0] - n[0] * travel, pos[1] - n[1] * travel, pos[2] - n[2] * travel];
}

export interface ImpactExtraInput {
  readonly position: readonly [number, number, number];
  readonly energy: number;
  readonly profile: ParticleMaterialProfile;
  readonly burstIndex: number;
  readonly surfaceNormal: readonly [number, number, number];
  readonly impact?: ImpactParams | null;
  readonly colliders: readonly BoxCollider[];
}

export function plannedShockRingCount(energy: number, blast: number): number {
  const b = clamp01(blast);
  if (b <= 0) return 0;
  const e = energyLaunch(energy);
  return Math.max(0, Math.round((36 + 48 * e) * b));
}

export function plannedBackSpallFineCount(
  energy: number,
  intensity: number,
  blast: number,
  thin: boolean,
): { back: number; front: number } {
  if (!thin) return { back: 0, front: 0 };
  const e = energyLaunch(energy);
  const i = clamp01(intensity);
  const b = clamp01(blast);
  const kinetic = i >= SPALL_INTENSITY_THRESHOLD
    ? 14 + 18 * e * ((i - SPALL_INTENSITY_THRESHOLD) / (1 - SPALL_INTENSITY_THRESHOLD))
    : 0;
  const chemical = b * (20 + 26 * e);
  const back = Math.round(kinetic + chemical * 0.58);
  const front = b > 0 ? Math.round(chemical * 0.42) : 0;
  return { back, front };
}

export function plannedBackSpallChipCount(
  energy: number,
  intensity: number,
  blast: number,
  thin: boolean,
): { back: number; front: number } {
  if (!thin) return { back: 0, front: 0 };
  const e = energyLaunch(energy);
  const i = clamp01(intensity);
  const b = clamp01(blast);
  const kinetic = i >= SPALL_INTENSITY_THRESHOLD ? Math.round(3 + 5 * e) : 0;
  const chemical = b > 0 ? Math.round((2 + 4 * e) * b) : 0;
  return {
    back: kinetic + (b > 0 ? chemical : 0),
    front: b > 0 ? Math.max(2, chemical) : 0,
  };
}

function makeSpallFine(
  px: number,
  py: number,
  pz: number,
  vx: number,
  vy: number,
  vz: number,
  size: number,
  linger: number,
  tint: readonly [number, number, number],
  seed: number,
  depositMass: number,
  origin: readonly [number, number, number],
  energy: number,
  buoyancy: number,
  nx: number,
  ny: number,
  nz: number,
  upFactor: number,
): CloudSpawnRecord {
  return {
    x: px,
    y: py,
    z: pz,
    vx,
    vy,
    vz,
    size,
    seed,
    kind: SPALL_FINE_KIND,
    depositMass,
    ox: origin[0],
    oy: origin[1],
    oz: origin[2],
    energy,
    r: tint[0],
    g: tint[1],
    b: tint[2],
    linger,
    buoyancy,
    nx,
    ny,
    nz,
    upFactor,
  };
}

function emitSpallCone(
  count: number,
  origin: readonly [number, number, number],
  axis: readonly [number, number, number],
  profile: ParticleMaterialProfile,
  rng: () => number,
  storedEnergy: number,
  depositEach: number,
  speed0: number,
  aperture: number,
): CloudSpawnRecord[] {
  if (count <= 0) return [];
  const n = normalize3(axis);
  const tints = profile.cloudTints;
  const out: CloudSpawnRecord[] = [];
  for (let i = 0; i < count; i += 1) {
    const jitter = 0.03 + 0.04 * rng();
    const px = origin[0] + n[0] * 0.03 + (rng() * 2 - 1) * jitter;
    const py = origin[1] + n[1] * 0.03 + (rng() * 2 - 1) * jitter * 0.35;
    const pz = origin[2] + n[2] * 0.03 + (rng() * 2 - 1) * jitter;
    const dir = coneAround(rng, n, aperture);
    const speed = speed0 * (0.55 + rng() * 0.7);
    const tint = tints[tints.length > 1 && rng() > 0.55 ? 1 : 0];
    out.push(makeSpallFine(
      px,
      py,
      pz,
      dir[0] * speed,
      dir[1] * speed,
      dir[2] * speed,
      0.08 + rng() * 0.12,
      0.55 + rng() * 0.45,
      tint,
      rng(),
      depositEach,
      origin,
      storedEnergy,
      profile.cloudBuoyancy * 0.35,
      n[0],
      n[1],
      n[2],
      0,
    ));
  }
  return out;
}

const CHIP_CLAY: [number, number, number] = [0.52, 0.22, 0.14];
const CHIP_MORTAR: [number, number, number] = [0.40, 0.38, 0.35];

function randomUnitQuat(rng: () => number): [number, number, number, number] {
  const u1 = rng();
  const u2 = rng();
  const u3 = rng();
  const s1 = Math.sqrt(1 - u1);
  const s2 = Math.sqrt(u1);
  return [
    s1 * Math.sin(2 * Math.PI * u2),
    s1 * Math.cos(2 * Math.PI * u2),
    s2 * Math.sin(2 * Math.PI * u3),
    s2 * Math.cos(2 * Math.PI * u3),
  ];
}

function emitSpallChips(
  count: number,
  origin: readonly [number, number, number],
  axis: readonly [number, number, number],
  energy: number,
  rng: () => number,
  chipMass: number,
): ChunkSpawnRecord[] {
  if (count <= 0 || chipMass <= 0) return [];
  const n = normalize3(axis);
  const e = energyLaunch(energy);
  const records: ChunkSpawnRecord[] = [];
  const volumes = new Float32Array(count);
  const sizes: Array<[number, number, number]> = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const long = 0.006 + rng() * 0.010;
    const thick = long * (0.28 + rng() * 0.25);
    const width = long * (0.45 + rng() * 0.40);
    sizes[i] = [long, thick, width];
    volumes[i] = long * thick * width;
  }
  let volSum = 0;
  for (let i = 0; i < count; i += 1) volSum += volumes[i];
  for (let i = 0; i < count; i += 1) {
    const [sx, sy, sz] = sizes[i];
    const launch = coneAround(rng, n, 0.42);
    const speed = (2.2 + e * 2.6) * (0.55 + rng() * 0.55);
    const [qx, qy, qz, qw] = randomUnitQuat(rng);
    const tumble = 10 + rng() * 14;
    const tint = rng() < 0.3 ? CHIP_MORTAR : CHIP_CLAY;
    const jitter = 0.015 + rng() * 0.025;
    records.push({
      x: origin[0] + n[0] * 0.04 + (rng() * 2 - 1) * jitter,
      y: origin[1] + n[1] * 0.04 + (rng() * 2 - 1) * jitter * 0.35,
      z: origin[2] + n[2] * 0.04 + (rng() * 2 - 1) * jitter,
      vx: launch[0] * speed,
      vy: launch[1] * speed,
      vz: launch[2] * speed,
      hx: sx * 0.5,
      hy: sy * 0.5,
      hz: sz * 0.5,
      wx: (rng() * 2 - 1) * tumble,
      wy: (rng() * 2 - 1) * tumble * 0.7,
      wz: (rng() * 2 - 1) * tumble,
      qx,
      qy,
      qz,
      qw,
      r: tint[0],
      g: tint[1],
      b: tint[2],
      mass: chipMass * (volumes[i] / Math.max(volSum, 1e-12)),
      restitution: 0.28,
      friction: 0.62,
      releaseAt: 0,
      seed: rng(),
      maxBounces: 2,
      persistFor: 3 + rng() * 2,
    });
  }
  return records;
}

/**
 * Secondary fines + chips on the far face (and, when blast > 0, a small
 * front-face cone). Empty when intensity is below threshold AND blast is 0,
 * or when the hit collider is too thick.
 */
export function planBackSpall(input: ImpactExtraInput): {
  fines: CloudSpawnRecord[];
  chips: ChunkSpawnRecord[];
} {
  const imp = resolveImpact(input.impact);
  const box = nearestCollider(input.position, input.colliders);
  const thin = box ? slabThicknessAlongNormal(box, input.surfaceNormal) <= THIN_WALL_MAX_DEPTH : false;
  if (!shouldBackSpall(imp, thin) || !box) return { fines: [], chips: [] };

  const counts = plannedBackSpallFineCount(input.energy, imp.intensity, imp.blastFraction, thin);
  // Energy/thin-wall counts do not read signature.chipCap — zero those here.
  const chipsAllowed = input.profile.signature.chipCap > 0
    && input.profile.signature.chipFraction > 0;
  const chipCounts = chipsAllowed
    ? plannedBackSpallChipCount(input.energy, imp.intensity, imp.blastFraction, thin)
    : { back: 0, front: 0 };
  if (counts.back + counts.front + chipCounts.back + chipCounts.front === 0) {
    return { fines: [], chips: [] };
  }

  const rng = mulberry32(seedFromBurst(input.burstIndex) ^ 0x5a11ed01);
  const n = normalize3(input.surfaceNormal);
  const backN: [number, number, number] = [-n[0], -n[1], -n[2]];
  const backPos = backOriginFromHit(input.position, n, box);
  const e = energyLaunch(input.energy);
  const stored = clampEnergy(input.energy);
  const speed0 = 1.15 + e * 2.4;
  const aperture = 0.28 + (1 - imp.iHi) * 0.18;
  const totalFines = Math.max(1, counts.back + counts.front);
  const depositEach = 0.012 / totalFines;
  const fines = [
    ...emitSpallCone(
      counts.back,
      backPos,
      backN,
      input.profile,
      rng,
      stored,
      depositEach,
      speed0,
      aperture,
    ),
    ...emitSpallCone(
      counts.front,
      input.position,
      n,
      input.profile,
      rng,
      stored,
      depositEach,
      speed0 * 0.85,
      aperture + 0.08,
    ),
  ];
  const chipMass = 0.018;
  const chips = [
    ...emitSpallChips(chipCounts.back, backPos, backN, input.energy, rng, chipMass * 0.55),
    ...emitSpallChips(chipCounts.front, input.position, n, input.energy, rng, chipMass * 0.45),
  ];
  return { fines, chips };
}

/**
 * Pressure-wave dust ring on the floor around the impact's XZ projection.
 * Empty at blast 0. Radius scales with energyLaunch × energyRadius.
 */
export function planShockRing(input: ImpactExtraInput): CloudSpawnRecord[] {
  const imp = resolveImpact(input.impact);
  const count = plannedShockRingCount(input.energy, imp.blastFraction);
  if (count <= 0) return [];
  const rng = mulberry32(seedFromBurst(input.burstIndex) ^ 0x5140c4);
  const e = energyLaunch(input.energy);
  const rScale = energyRadius(input.energy);
  const r0 = (0.62 + 1.05 * e) * rScale * (0.55 + 0.85 * imp.blastFraction);
  const stored = clampEnergy(input.energy);
  const origin = input.position;
  const tints = input.profile.cloudTints;
  const depositEach = 0.02 / count;
  const records: CloudSpawnRecord[] = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const phi = ((i + rng()) / count) * Math.PI * 2;
    const rad = r0 * (0.86 + 0.28 * rng());
    const cp = Math.cos(phi);
    const sp = Math.sin(phi);
    const px = origin[0] + cp * rad;
    const pz = origin[2] + sp * rad;
    const py = 0.035 + rng() * 0.055;
    const outSpeed = (1.15 + 1.85 * e) * (0.55 + rng() * 0.6);
    const upSpeed = (0.95 + 1.55 * e) * (0.45 + rng() * 0.7);
    const tint = tints[tints.length > 1 && rng() > 0.6 ? 1 : 0];
    records[i] = {
      x: px,
      y: py,
      z: pz,
      vx: cp * outSpeed,
      vy: upSpeed,
      vz: sp * outSpeed,
      size: 0.10 + rng() * 0.14,
      seed: rng(),
      kind: RING_FINE_KIND,
      depositMass: depositEach,
      ox: origin[0],
      oy: 0,
      oz: origin[2],
      energy: stored,
      r: tint[0],
      g: tint[1],
      b: tint[2],
      linger: 0.85 + rng() * 0.55,
      buoyancy: input.profile.cloudBuoyancy * 0.2,
      nx: 0,
      ny: 1,
      nz: 0,
      upFactor: 1,
    };
  }
  return records;
}

export interface ImpactExtras {
  readonly fines: CloudSpawnRecord[];
  readonly chips: ChunkSpawnRecord[];
  readonly backSpallFines: number;
  readonly backSpallChips: number;
  readonly ringFines: number;
}

export function planImpactExtras(input: ImpactExtraInput): ImpactExtras {
  const imp = resolveImpact(input.impact);
  if (imp.identity) {
    return { fines: [], chips: [], backSpallFines: 0, backSpallChips: 0, ringFines: 0 };
  }
  const spall = planBackSpall(input);
  const ring = planShockRing(input);
  return {
    fines: [...spall.fines, ...ring],
    chips: spall.chips,
    backSpallFines: spall.fines.length,
    backSpallChips: spall.chips.length,
    ringFines: ring.length,
  };
}

export function plannedImpactExtraFineCount(input: ImpactExtraInput): number {
  const imp = resolveImpact(input.impact);
  if (imp.identity) return 0;
  const thin = isThinHit(input.position, input.surfaceNormal, input.colliders);
  const spall = plannedBackSpallFineCount(input.energy, imp.intensity, imp.blastFraction, thin);
  return spall.back + spall.front + plannedShockRingCount(input.energy, imp.blastFraction);
}
